import { Video } from "@/api";
import { useCallback, useEffect, useRef } from "react";
import { getCurrentDateTime } from "@/utils";
import { usePutProgressEvent } from "@/app/modules/ContentDetails/hooks/usePutProgressEvent.tsx";

interface UseProgressTrackingProps {
  video: Video;
}

export const useProgressTracking = ({ video }: UseProgressTrackingProps) => {
  const { mutate: putProgressMutate } = usePutProgressEvent();
  const intervalIdRef = useRef<number>(0);

  useEffect(() => {
    // Clear interval on unmount
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const sendProgress = useCallback(
    (player: HTMLVideoElement) => {
      const currentTime = Math.floor(player?.currentTime ?? 0);
      const duration = player?.duration ? Math.floor(player.duration) : 0;
      const percentageComplete =
        duration > 0 ? Math.round((currentTime / duration) * 100) : 0;

      console.debug(
        "SendProgress",
        `${currentTime.toString()} seconds`,
        `${percentageComplete.toString()}%`,
      );

      const isUpcomingOrActiveLiveVideo =
        !!video.Properties?.LiveEventStartDate &&
        !video.Properties?.LiveEventEnded;

      putProgressMutate({
        ItemId: video.VideoId,
        ItemType: "video",
        SourceId: video.SourceId,
        DataSource: video.DataSource,
        ProgressSeconds: currentTime.toString(),
        PercentComplete: percentageComplete.toString(),
        Date: getCurrentDateTime(),
        CustomerEmail: "none@vidapp.com", // Should be non-mandatory
        Type: isUpcomingOrActiveLiveVideo ? "live" : "",
      });
    },
    [
      putProgressMutate,
      video.DataSource,
      video.SourceId,
      video.VideoId,
      video.Properties,
    ],
  );

  const startProgress = useCallback(
    (player: HTMLVideoElement) => {
      if (!intervalIdRef.current) {
        intervalIdRef.current = window.setInterval(() => {
          // Only record progress tracking if the video is playing
          if (player && !player.paused) {
            sendProgress(player);
          }
        }, 60000); // 1 minute
      }
    },
    [sendProgress],
  );
  const stopProgress = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
  }, [intervalIdRef]);

  return { sendProgress, startProgress, intervalIdRef, stopProgress };
};
